import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSEO } from "../lib/seo";

export default function PrivacyPage() {
    useSEO({
        title: "Informativa Privacy",
        description: "Informativa sulla privacy ai sensi del GDPR (Reg. UE 2016/679) dello Studio Legale Avv. Marco Candido: titolare, finalità, diritti dell'interessato.",
        path: "/privacy",
    });
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <div data-testid="privacy-page" className="min-h-screen bg-white">
            <Header />
            <main className="pt-32 pb-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    <Link to="/" data-testid="privacy-back" className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold-deep mb-12 transition-colors">
                        <ArrowLeft size={16} /> Torna alla home
                    </Link>

                    <div className="flex items-center gap-4 mb-6">
                        <span className="gold-rule" />
                        <span className="label-eyebrow">Informativa</span>
                    </div>

                    <h1 className="font-serif text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-4">
                        Informativa sulla Privacy
                    </h1>
                    <p className="text-slate-500 text-sm mb-12">Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003.<br />Ultimo aggiornamento: maggio 2026</p>

                    <div className="article-content">
                        <h2>1. Titolare del trattamento</h2>
                        <p>Il Titolare del trattamento è l'<strong>Avv. Marco Candido</strong>, con studio in Corso Vittorio Emanuele II, 142/D — 29121 Piacenza (PC). Email: <a href="mailto:marco.candido@pabclex.it" className="text-gold-deep underline">marco.candido@pabclex.it</a> — Tel: <a href="tel:0523468737" className="text-gold-deep underline">0523 468737</a>.</p>

                        <h2>2. Tipologie di dati raccolti</h2>
                        <p>Attraverso il modulo di contatto presente su questo sito vengono raccolti i seguenti dati personali, forniti volontariamente dall'utente:</p>
                        <ul>
                            <li>Nome e cognome</li>
                            <li>Indirizzo email</li>
                            <li>Numero di telefono (facoltativo, se richiesto il richiamo)</li>
                            <li>Oggetto della richiesta</li>
                            <li>Contenuto del messaggio</li>
                            <li>Preferenze di contatto (opzione di richiamo telefonico)</li>
                        </ul>

                        <h2>3. Finalità e base giuridica del trattamento</h2>
                        <p>I dati sono trattati esclusivamente per le seguenti finalità:</p>
                        <ul>
                            <li><strong>Riscontro alla richiesta:</strong> rispondere alle domande poste tramite il modulo di contatto e valutare un'eventuale prestazione professionale (base giuridica: art. 6 par. 1 lett. b GDPR — misure precontrattuali).</li>
                            <li><strong>Adempimento di obblighi di legge</strong> (base giuridica: art. 6 par. 1 lett. c GDPR), inclusi gli obblighi deontologici dell'avvocatura.</li>
                        </ul>
                        <p>Il conferimento dei dati è facoltativo; tuttavia, il rifiuto di fornire i dati contrassegnati come obbligatori rende impossibile dare seguito alla richiesta.</p>

                        <h2>4. Modalità di trattamento</h2>
                        <p>Il trattamento avviene con strumenti elettronici e cartacei, nel rispetto delle misure di sicurezza tecniche e organizzative previste dagli artt. 24, 25 e 32 GDPR. I dati sono trattati esclusivamente dal Titolare e dai suoi collaboratori autorizzati, vincolati al segreto professionale.</p>

                        <h2>5. Periodo di conservazione</h2>
                        <p>I dati raccolti tramite il modulo di contatto sono conservati per il tempo strettamente necessario a dare riscontro alla richiesta e, in caso di instaurazione del rapporto professionale, per il periodo previsto dalla normativa fiscale, civilistica e deontologica (di norma 10 anni).</p>

                        <h2>6. Destinatari dei dati</h2>
                        <p>I dati non saranno diffusi né comunicati a terzi, salvo:</p>
                        <ul>
                            <li>obblighi di legge o richieste dell'autorità giudiziaria;</li>
                            <li>fornitori di servizi tecnici (es. provider di posta elettronica transazionale, hosting) designati come responsabili del trattamento ex art. 28 GDPR.</li>
                        </ul>

                        <h2>7. Cookie</h2>
                        <p>Questo sito utilizza esclusivamente <strong>cookie tecnici</strong> strettamente necessari al funzionamento del sito stesso. Non vengono utilizzati cookie di profilazione né di marketing. Il consenso espresso tramite il banner riguarda esclusivamente la presa visione dell'informativa.</p>

                        <h2>8. Diritti dell'interessato</h2>
                        <p>In qualsiasi momento l'interessato può esercitare i diritti previsti dagli artt. 15-22 GDPR:</p>
                        <ul>
                            <li>diritto di accesso ai propri dati;</li>
                            <li>diritto di rettifica;</li>
                            <li>diritto alla cancellazione (oblio), nei limiti di legge;</li>
                            <li>diritto di limitazione del trattamento;</li>
                            <li>diritto alla portabilità dei dati;</li>
                            <li>diritto di opposizione;</li>
                            <li>diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-gold-deep underline">www.garanteprivacy.it</a>).</li>
                        </ul>
                        <p>Per esercitare tali diritti è possibile scrivere a <a href="mailto:marco.candido@pabclex.it" className="text-gold-deep underline">marco.candido@pabclex.it</a>.</p>

                        <h2>9. Trasferimento dati extra-UE</h2>
                        <p>I dati non vengono trasferiti al di fuori dell'Unione Europea. Eventuali fornitori extra-UE garantiscono, ove utilizzati, adeguate misure di tutela (clausole contrattuali standard ex art. 46 GDPR).</p>

                        <h2>10. Modifiche all'informativa</h2>
                        <p>Il Titolare si riserva di modificare la presente informativa per adeguarla a nuove disposizioni di legge. Le modifiche saranno pubblicate su questa pagina.</p>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
